// Hatha Yoga Frome - Rewritten with natural yoga-specific content
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock, Users, MapPin } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { usePageMeta } from "../../components/shared/SEOHelper";

const HathaYogaFrome = () => {
  usePageMeta({
    title: "Hatha Yoga Frome | Traditional Yoga Classes | Annelie Lily",
    description: "Traditional Hatha yoga classes in Frome, Somerset. Standing poses, stretches, twists and backbends with breathing at the centre.",
    canonical: "https://annelielily.com/yoga-classes/hatha-yoga"
  });

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      <PageBreadcrumb currentPage="Hatha Yoga" currentSlug="yoga-classes/hatha-yoga" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
                Hatha Yoga in Frome
              </span>
              <h1 className="font-heading text-4xl md:text-5xl font-light text-neutral-800 mb-5">
                Hatha Yoga Classes
              </h1>
              <p className="text-neutral-700 leading-relaxed mb-6">
                This is probably what you picture when you think of yoga. Standing poses, 
                seated stretches, twists, gentle backbends — flowing one into the next, 
                with breathing at the centre of everything.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" className="btn-primary">
                  Book a Class <ArrowRight className="w-4 h-4 inline ml-2" />
                </Link>
              </div>
              
              <div className="flex flex-wrap gap-4 mt-6 text-sm text-neutral-600">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gold" />
                  75-90 minutes
                </span>
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gold" />
                  Small groups
                </span>
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gold" />
                  Frome, Somerset
                </span>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/ktg7cngr_IMG_1846.jpg"
                  alt="Annelie Lily demonstrating reverse warrior pose (Viparita Virabhadrasana) on a beach at low tide"
                  className="w-full h-full object-contain bg-muted"
                  width={800}
                  height={800}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* What Happens in Class */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              What Happens in a Hatha Class
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                We start lying down or in a comfortable seated position, taking a few minutes to arrive 
                and let go of whatever you've brought with you from outside. I'll guide you through some 
                simple breathing to settle the nervous system.
              </p>
              <p>
                Then we build gradually — some standing work for strength and stability, seated poses 
                for hips and hamstrings, twists for the spine, maybe a gentle inversion if the group 
                is up for it. I adapt each class to who's in the room. If you're <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">new to yoga</Link>, 
                don't worry — I'll guide you through everything.
              </p>
              <p>
                Every class ends with a proper relaxation. Not just a few minutes lying there waiting 
                to leave, but a guided process that helps the body integrate what we've done. Sometimes 
                I'll take you through yoga nidra, sometimes a simpler savasana — but either way, you 
                won't be rushed back into the world.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Who It's For */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
              Who Comes to Hatha?
            </h2>
            <div className="bg-white/70 p-6 md:p-8 rounded-xl border border-gold/10">
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Complete beginners</strong> — this is a good place to start. You'll learn the basics properly without feeling lost.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>People returning after a break</strong> — had a baby, been ill, life got busy. Hatha gives you a way back in.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Anyone who sits at a desk all day</strong> — tight shoulders, stiff hips, sore lower back. We address all of that.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>Runners and cyclists</strong> — I get a lot of athletes who come for the stretching but stay for the mental benefits.</span>
                </li>
                <li className="flex items-start gap-3 text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span><strong>People wanting a balanced practice</strong> — not just stretching, not just strength, but both together.</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Practical Info */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              The Practical Bits
            </h2>
            <div className="space-y-4 text-neutral-700">
              <p>
                <strong>What to bring:</strong> A yoga mat if you have one (I have spares). Comfortable 
                clothes that won't ride up or restrict you. A bottle of water. Maybe a blanket or jumper 
                for relaxation — you can get cold lying still.
              </p>
              <p>
                <strong>What to eat:</strong> Don't come on a full stomach. A light snack an hour or two 
                before is fine, but twisting on a belly full of lunch is no fun for anyone.
              </p>
              <p>
                <strong>Arriving:</strong> Come a few minutes early if you can, especially your first time. 
                It gives us a chance to chat about any injuries or concerns, and you can settle in without rushing.
              </p>
              <p>
                <strong>Class sizes:</strong> Maximum 8 people. Small enough that I can see what's happening 
                and offer individual guidance, but big enough that you're not on your own.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Common Questions */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
              Questions People Ask
            </h2>
            <div className="space-y-4">
              <div className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">I'm not flexible — can I still do this?</h3>
                <p className="text-neutral-600 text-sm">
                  Yes. Flexibility isn't a prerequisite for yoga — it's something that develops over time. 
                  I offer easier versions of every pose, and we use props to make things accessible. You 
                  work where your body is today, not where you think it should be.
                </p>
              </div>

              <div className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">What's the difference between Hatha and Yin?</h3>
                <p className="text-neutral-600 text-sm">
                  Hatha is more active — we move between poses, build heat, work the muscles. <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin yoga</Link> is 
                  passive — we hold poses for minutes at a time, targeting deeper connective tissue. 
                  Many people do both because they complement each other so well.
                </p>
              </div>

              <div className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">How often should I come?</h3>
                <p className="text-neutral-600 text-sm">
                  Once a week is a good start. You'll feel the benefits, and it's sustainable for most 
                  schedules. Some people build up to twice a week as yoga becomes part of their routine. 
                  There's no right answer — whatever you can manage consistently is better than nothing.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
                Ready to Try Hatha?
              </h2>
              <p className="text-neutral-600 mb-6">
                Get in touch to find out when the next class is and whether it's right for you. 
                No commitment, no pressure — just honest conversation about what might help.
              </p>
              <Link to="/contact" className="btn-primary">
                Get in Touch <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/70 p-6 rounded-xl border border-gold/10"
            >
              <h3 className="font-heading text-lg text-neutral-800 mb-4">Also Available</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/yoga-classes/yin-yoga" className="text-neutral-600 hover:text-gold transition-colors flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" /> Yin Yoga — for deeper, slower release
                  </Link>
                </li>
                <li>
                  <Link to="/yoga-classes/private-yoga" className="text-neutral-600 hover:text-gold transition-colors flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" /> Private sessions — 1-to-1 attention
                  </Link>
                </li>
                <li>
                  <Link to="/services" className="text-neutral-600 hover:text-gold transition-colors flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" /> View all services
                  </Link>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Areas */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">
            Hatha yoga for your area:
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Hatha Yoga Bath", link: "/areas/yoga-bath/hatha-yoga" },
              { name: "Hatha Yoga Trowbridge", link: "/areas/yoga-trowbridge/hatha-yoga" },
              { name: "Hatha Yoga Warminster", link: "/areas/yoga-warminster/hatha-yoga" },
              { name: "Hatha Yoga Wells", link: "/areas/yoga-wells/hatha-yoga" },
              { name: "Hatha Yoga Radstock", link: "/areas/yoga-radstock/hatha-yoga" },
              { name: "Hatha Yoga Westbury", link: "/areas/yoga-westbury/hatha-yoga" },
              { name: "Hatha Yoga Shepton Mallet", link: "/areas/yoga-shepton-mallet/hatha-yoga" },
            ].map((area) => (
              <Link
                key={area.name}
                to={area.link}
                className="text-sm text-gold hover:underline underline-offset-4"
              >
                {area.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HathaYogaFrome;
